// A dialog for choosing one thing from a server-rendered list, styled like the
// command palette (the recruiter picker on Job/Add). Opens and closes like
// modal('someId'); `load` fetches the list for whatever is in the search box
// and renders [role=option] rows into x-ref="list", which the view's script
// already knows how to do. Arrow keys move, Enter picks (clicks the row).
const SEARCH_DELAY = 250;

export default (id, load) => ({
  open: false,
  query: '',
  active: 0,
  loading: false,
  timer: null,
  opener: null,

  init() {
    window.addEventListener('modal:open', (event) => {
      if (event.detail === id) this.show();
    });
    window.addEventListener('modal:close', (event) => {
      if (event.detail === id) this.close();
    });

    this.$watch('query', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), SEARCH_DELAY);
    });

    // New rows (a search or a page of results): start again from the top.
    new MutationObserver(() => {
      this.active = 0;
      this.highlight();
      this.$refs.list.querySelector('.palette-list')?.scrollTo(0, 0);
    }).observe(this.$refs.list, { childList: true });
  },

  show() {
    this.opener = document.activeElement;
    this.open = true;
    this.refresh();
    this.$nextTick(() => this.$refs.input.select());
  },

  // Back to whatever opened it, e.g. the Select Recruiter button.
  close() {
    clearTimeout(this.timer);
    this.open = false;
    this.$nextTick(() => this.opener?.focus?.());
  },

  refresh() {
    this.loading = true;
    Promise.resolve(load()).finally(() => {
      this.loading = false;
    });
  },

  options() {
    return [...this.$refs.list.querySelectorAll('[role="option"]')];
  },

  highlight(scroll = false) {
    this.options().forEach((option, index) => {
      option.setAttribute('aria-selected', index === this.active ? 'true' : 'false');
      if (index === this.active && scroll) option.scrollIntoView({ block: 'nearest' });
    });
  },

  get activeId() {
    return this.open ? this.options()[this.active]?.id ?? '' : '';
  },

  hover(event) {
    const index = this.options().indexOf(event.target.closest('[role="option"]'));
    if (index !== -1 && index !== this.active) {
      this.active = index;
      this.highlight();
    }
  },

  onKeydown(event) {
    const count = this.options().length;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (count === 0) return;
      this.active = (this.active + (event.key === 'ArrowDown' ? 1 : count - 1)) % count;
      this.highlight(true);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.options()[this.active]?.click();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  },
});
